import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MARKET_COPY } from "@/lib/market/copy";
import type {
  MarketFilterChip,
  MarketFilterChipKind,
} from "@/lib/market/browseState";

interface MarketActiveFiltersProps {
  chips: MarketFilterChip[];
  onRemove: (kind: MarketFilterChipKind) => void;
  onClearAll: () => void;
}

/**
 * Active-filter chip row shown under the search bar (Phase 4, D4).
 * Presentation-only: chip data comes from `buildActiveFilterChips` via the
 * browse hook; removal/clear-all flow through `browseState` handlers.
 */
const MarketActiveFilters = React.memo(
  ({ chips, onRemove, onClearAll }: MarketActiveFiltersProps) => {
    if (chips.length === 0) return null;

    return (
      <div
        className="flex flex-wrap items-center gap-2 mb-6"
        aria-label={MARKET_COPY.activeFiltersLabel}
      >
        {chips.map((chip) => (
          <button
            key={chip.kind}
            type="button"
            onClick={() => onRemove(chip.kind)}
            className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground text-sm pl-3 pr-2 py-1 hover:bg-tertiary transition-colors"
            aria-label={`${MARKET_COPY.removeFilterPrefix} ${chip.label} ${MARKET_COPY.removeFilterSuffix}`}
          >
            <span className="max-w-[12rem] truncate">{chip.label}</span>
            <XMarkIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          </button>
        ))}
        <button
          type="button"
          onClick={onClearAll}
          className="text-sm text-primary hover:underline px-1 py-1"
        >
          {MARKET_COPY.clearAllFilters}
        </button>
      </div>
    );
  },
);
MarketActiveFilters.displayName = "MarketActiveFilters";

export default MarketActiveFilters;
